import i18n from 'i18next'
import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useBMStore } from '../stores/useBMStore'

import { NewDashboardUI } from '../features/Dashboard/routes/NewDashboard'
import { DetailedReport } from '../features/DetailedReport'
import {
  AccountLogoResponse,
  GetDomainsResponse,
  PlansDetailsType,
  UserProfileResponse,
} from '../types/federatedContext'

interface FederatedContextProps {
  useGetDomains: () => { data?: GetDomainsResponse }
  useGetAccountLogo: () => AccountLogoResponse
  useGetUserProfile: () => {
    data?: UserProfileResponse
  }
  useGetPlansDetails: () => PlansDetailsType
}

const FederatedContext: React.FC<FederatedContextProps> = ({
  useGetDomains,
  useGetUserProfile,
  useGetAccountLogo,
  useGetPlansDetails,
}) => {
  const { pathname } = useLocation()
  const domainsResponse = useGetDomains()
  const userSettings = useGetUserProfile()
  const accountLogoData = useGetAccountLogo()
  const plansDetails = useGetPlansDetails()

  const setDomains = useBMStore((state) => state.setDomains)
  const setChosenDomains = useBMStore((state) => state.setChosenDomains)
  const setHasFetchedDomains = useBMStore((state) => state.setHasFetchedDomains)
  const currentDomains = useBMStore((state) => state.domains)
  const currentChosenDomains = useBMStore((state) => state.chosenDomains)

  const userPlans = useMemo(() => plansDetails?.data?.plans ?? [], [plansDetails?.data?.plans])

  const accountLogoDownloadLink = useMemo(
    () => accountLogoData?.data?.downloadLink ?? '',
    [accountLogoData?.data?.downloadLink]
  )

  const domains = useMemo(
    () => domainsResponse.data?.data.domains ?? [],
    [domainsResponse.data?.data.domains]
  )

  useEffect(() => {
    const filteredDomains = domains.filter((domain) => domain.botZapping)

    setHasFetchedDomains(true) // Mark fetch complete

    const currentAddrs = currentDomains
      .map((d) => d.address)
      .sort()
      .join(',')
    const newAddrs = filteredDomains
      .map((d) => d.address)
      .sort()
      .join(',')

    if (newAddrs !== currentAddrs) {
      setDomains(filteredDomains)
    }

    if (filteredDomains.length === 1) {
      const [newDomain] = filteredDomains
      const [currentDomain] = currentChosenDomains ?? []

      if (!currentDomain || currentDomain.address !== newDomain.address) {
        setChosenDomains([newDomain])
      }
    }
  }, [
    domains,
    currentDomains,
    currentChosenDomains,
    setDomains,
    setChosenDomains,
    setHasFetchedDomains,
  ])

  const locale = useMemo(() => {
    if (!(userSettings instanceof Error)) {
      return userSettings?.data?.locale
    } else {
      return 'en-UK'
    }
  }, [userSettings])

  const language = useMemo(() => {
    if (!(userSettings instanceof Error)) {
      return userSettings?.data?.language
    } else {
      return 'en'
    }
  }, [userSettings])

  useEffect(() => {
    if (language) {
      void i18n.changeLanguage(language)
    }
  }, [language])

  return (
    <>
      {pathname.includes('dashboard') ? (
        <NewDashboardUI
          locale={locale}
          userPlans={userPlans}
          accountLogoDownloadLink={accountLogoDownloadLink}
        />
      ) : (
        <DetailedReport locale={locale} />
      )}
    </>
  )
}

export default FederatedContext
